import { createContext, useContext } from 'react';
import { localize } from '@/content/curriculum';
import type { Localized } from '@/content/schema';
import type { Locale } from '@/domain/ids';
import { createTranslator, DIRECTION, formatNumber, LOCALE_TAG, type Translate } from './translate';

export interface I18n {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t: Translate;
  /** Localised content text with Arabic fallback. */
  l: (text: Localized) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatPercent: (ratio: number) => string;
  formatDate: (iso: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  formatMinutes: (ms: number) => string;
  formatList: (items: string[]) => string;
}

export const I18nContext = createContext<I18n | null>(null);

export function makeI18n(locale: Locale): I18n {
  const t = createTranslator(locale);
  return {
    locale,
    dir: DIRECTION[locale],
    t,
    l: (text) => localize(text, locale),
    formatNumber: (value, options) => formatNumber(locale, value, options),
    formatPercent: (ratio) =>
      formatNumber(locale, ratio, { style: 'percent', maximumFractionDigits: 0 }),
    formatDate: (iso, options = { day: 'numeric', month: 'long' }) =>
      new Intl.DateTimeFormat(LOCALE_TAG[locale], { calendar: 'gregory', ...options }).format(
        new Date(iso),
      ),
    formatMinutes: (ms) => t('units.minutes', { count: Math.round(ms / 60_000) }),
    formatList: (items) =>
      new Intl.ListFormat(LOCALE_TAG[locale], { type: 'conjunction' }).format(items),
  };
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>');
  return context;
}
